import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  DistributionOrganizationTariff,
  DistributionTariff,
  DistributionTariffRequestBody,
} from './types';

export const distributionTariffsApi = createApi({
  reducerPath: 'distributionTariffsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/distribution-tariffs',
  }),
  tagTypes: [
    'DistributionTariffs',
    'DistributionTariff',
    'DistributionOrganizationTariffs',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getDistributionOrganizationTariffsI: builder.infiniteQuery<
      DistributionOrganizationTariff,
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/organizations`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['DistributionOrganizationTariffs'],
    }),
    getDistributionOrganizationTariffs: builder.query<
      DistributionOrganizationTariff[],
      FiltersGrid | void
    >({
      query: (params = {}) => ({
        url: '/organizations',
        params,
      }),
      providesTags: () => ['DistributionOrganizationTariffs'],
    }),
    getDistributionTariffsI: builder.infiniteQuery<
      DistributionTariff,
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['DistributionTariffs'],
    }),
    getDistributionTariffs: builder.query<
      DistributionTariff[],
      FiltersGrid | void
    >({
      query: (params = {}) => ({
        url: '/query',
        params,
      }),
      providesTags: () => ['DistributionTariffs'],
    }),
    getDistributionTariff: builder.query<DistributionTariff, string>({
      query: tariffId => ({
        url: `/${tariffId}`,
      }),
      providesTags: () => ['DistributionTariff'],
    }),
    createDistributionTariff: builder.mutation<
      DistributionTariff,
      DistributionTariffRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['DistributionTariffs', 'DistributionTariff'],
    }),
    updateDistributionTariff: builder.mutation<
      DistributionTariff,
      DistributionTariffRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['DistributionTariffs', 'DistributionTariff'],
    }),
    deleteDistributionTariff: builder.mutation<void, string>({
      query: tariffId => ({
        url: `/${tariffId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DistributionTariffs'],
    }),
  }),
});

export const {
  useGetDistributionOrganizationTariffsQuery,
  useGetDistributionTariffsQuery,
  useGetDistributionTariffQuery,
  useCreateDistributionTariffMutation,
  useUpdateDistributionTariffMutation,
} = distributionTariffsApi;

export const useGetDistributionOrganizationTariffsInfiniteQuery =
  normalizeInfiniteQueryData(
    distributionTariffsApi.useGetDistributionOrganizationTariffsIInfiniteQuery,
  );

export const useGetDistributionTariffsInfiniteQuery =
  normalizeInfiniteQueryData(
    distributionTariffsApi.useGetDistributionTariffsIInfiniteQuery,
  );
